import React, { useState, useRef } from 'react';

import { MESSAGE_MODALS } from '../../context/custom/MessageModalContext';
import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { useEntidad, useLista, useManagedContext, useFilterPersistence, useNavigateWithFilters } from '../../components/hooks';
import { ServerRequest } from '../../utils/apiweb';
import { createSearchWithSavedFilters } from '../../utils/searchWithSavedFilters';
import {
    Loading,
    TableCustom,
    SectionHeading,
    WrapperAdvancedSearch
} from '../../components/common';
import DataTaggerModalApi from '../../components/controls/DataTaggerModalApi';
import { PERMISSIONS } from '../../consts/permissions';
import { usePermissions } from '../../components/hooks/usePermissions';
import {TIPS} from "../../consts/tips";


function SubTasasView() {

    const { showMessageModal } = useManagedContext()

    //hooks
    const [state, setState] = useState({
        loading: false,
        showForm: false,
        dataTagger: {
            showModal: false,
            idEntidad: null
        },
        list: []
    });

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.TASA_EDIT);

    const searchRef = useRef();
    const tableRef = useRef();
    const [tableVisible, setTableVisible] = useState(false);

    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
        .then((error) => {
          const message = error.message;
          ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
          setState(prevState => {
            return {...prevState, loading: false};
          });
        })
        .catch((error) => {
            const message = 'Error procesando respuesta: ' + error;
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            setState(prevState => {
              return {...prevState, loading: false};
            });
        });
    }
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => {
          return {...prevState, loading: false};
        });
    }  

    const SearchSubTasasWithSavedFilters = createSearchWithSavedFilters({
        setState,
        setTableVisible,
        apiUrl: APIS.URLS.SUB_TASA,
        buildParamsUrl: (filters) => 
            `/filter?` +
            `idTasa=${filters.idTasa}&` +
            `codigo=${encodeURIComponent(filters.codigo)}&` +
            `descripcion=${encodeURIComponent(filters.descripcion)}&` +
            `etiqueta=${encodeURIComponent(filters.etiqueta)}`,
        callbackNoSuccess,
        callbackError
    });

    const { getCurrentFilters, restoredPageIndex } = useFilterPersistence(
        searchRef,
        tableRef,
        SearchSubTasasWithSavedFilters
    );
    const navigateWithFilters = useNavigateWithFilters(getCurrentFilters);
    
    const [, getRowLista ] = useLista({
        listas: ['TipoTributo'],
        onLoaded: (listas, isSuccess, error) => {
          if (isSuccess) {
            setState(prevState => ({...prevState}));
          }
          else {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
          }
        },
        memo: {
          key: 'TipoTributo',
          timeout: 0
        }
    });

    const [, getRowEntidad] = useEntidad({
        entidades: ['Tasa'],
        onLoaded: (entidades, isSuccess, error) => {
          if (isSuccess) {
            setState(prevState => ({...prevState}));
          }
          else {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
          }
        },
        memo: {
          key: 'Tasa',
          timeout: 0
        }
      });

    const getDescTipoTributo = (idTasa) => {
        const rowTasa = getRowEntidad('Tasa', idTasa);
        const row = getRowLista('TipoTributo', rowTasa.idTipoTributo);
        return (row) ? row.nombre : '';
    }

    const getDescTasa = (id) => {
        const row = getRowEntidad('Tasa', id);
        return (row) ? `${row.codigo} - ${row.descripcion}` : '';
    }    

    //definiciones
    const tableColumns = [
        { Header: 'Tasa', Cell: (props) => {
            const row = props.row.original;
            const descripcionTasa = getDescTasa(row.idTasa);
            return descripcionTasa;
          }, accessor: 'descripcionTasa', width: '30%' },        
        { Header: 'Sub tasa código', id: 'codigo', accessor: 'codigo', width: '15%' },
        { Header: 'Sub tasa descripción', accessor: 'descripcion', width: '30%' },
        { Header: 'Tipo de tributo', Cell: (props) => {
            const row = props.row.original;
            const tipoTributo = getDescTipoTributo(row.idTasa);
            return tipoTributo;
          }, accessor: 'tipoTributo', width: '15%' },
    ]; 


    //handles
    const avmr = {
        onAdd: () => {
            navigateWithFilters(`/sub-tasa/${OPERATION_MODE.NEW}`);
        },
        onView: ({ id }) => {
            navigateWithFilters(`/sub-tasa/${OPERATION_MODE.VIEW}/${id}`);
        },
        onModify: ({ id }) => {
            navigateWithFilters(`/sub-tasa/${OPERATION_MODE.EDIT}/${id}`);
        },
        onRemove: ({ id }) => {
            const subtasa = state.list.find(x => x.id === id)
            showMessageModal({
                ...MESSAGE_MODALS.BORRAR,
                message: `¿Está seguro de borrar la Sub tasa ${subtasa.codigo}?`,
                onConfirm: () => {
                    RemoveSubTasa(id)
                }
            })
        },
        onAdditionalInfo: ({ id }, disabled) => {
            setState(prevState => ({...prevState, dataTagger: { showModal: true, idEntidad: id, disabled }}))
        },
    }

    //funciones
    function SearchSubTasas() {
        SearchSubTasasWithSavedFilters(getCurrentFilters());
    }

    function RemoveSubTasa(id) {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const callbackSuccess = (response) => {
            response.json()
            .then((id) => {
                setState(prevState => {
                    return {...prevState, loading: false};
                });
                SearchSubTasas();
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
        };

        const paramsUrl = `/${id}`;

        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.SUB_TASA,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );        
    }



    const dataTaggerRow = state.list.find(x => x.id === state.dataTagger.idEntidad)
    const dataTaggerRegistro = dataTaggerRow ? `la sub tasa ${dataTaggerRow.descripcion}` : ''

    return (
    <>

        <Loading visible={state.loading}></Loading>

        {state.dataTagger.showModal &&
            <DataTaggerModalApi
                title="Información adicional de la sub tasa"
                registro={dataTaggerRegistro}
                entidad="SubTasa"
                tipName={TIPS.NAME.MODALS.INFORMACION_ADICIONAL_SUB_TASA_MODAL}
                idEntidad={state.dataTagger.idEntidad}
                disabled={state.dataTagger.disabled}
                onConfirm={() => {
                    setState(prevState => {
                        return {...prevState, dataTagger: {showModal: false, idEntidad: null}};
                    });
                }}
                onDismiss={() => {
                    setState(prevState => {
                        return {...prevState, dataTagger: {showModal: false, idEntidad: null}};
                    });
                }}
            />
        }

        <SectionHeading titles={[{ title: 'Entidades' }, { title: 'Sub tasas' }]}
                        onAddClick={canEdit ? avmr.onAdd : null} tableVisible={tableVisible} />

        <section className='section-accordion'>
            <WrapperAdvancedSearch
                ref={searchRef}
                onSearch={SearchSubTasas}
                filters={[
                    { title: 'Tasa', field: 'idTasa', type: 'tasa', size: 4 },
                    { title: 'Código', field: 'codigo', type: 'string', size: 1, searchOnEnter: true },
                    { title: 'Descripción', field: 'descripcion', type: 'string', size: 4, searchOnEnter: true },
                    { title: 'Etiqueta', field: 'etiqueta', type: 'string', size: 3, searchOnEnter: true }
                ]}
            />

            {tableVisible && (
                <TableCustom
                    ref={tableRef}
                    className={'TableCustomBase m-top-20'}
                    columns={tableColumns}
                    data={state.list}
                    avmr={avmr}
                    editCode={PERMISSIONS.TASA_EDIT}
                    restoredPageIndex={restoredPageIndex}
                    messageEmpty="No se encontraron resultados"
                />
            )}

        </section>
        
    </>
    )
}

export default SubTasasView;

